import { CModal, CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import qs from "qs";
import { useEffect, useReducer, useState } from "react";
import { useQuery } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import FilterIcon from "../../components/icons/FitlerIcon";
import FiltersModel from "../../components/ui/FiltersModel";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { apiConfig } from "../../config/apiConfig";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { Chip, removeEmptyFilterQueries } from "../../helpers/general";
import { controlStyle, usersFilters } from "../../helpers/constants";
import { useCookie } from "../../hooks/useCookie";
import { useDebounce } from "../../hooks/useDebounce";
import { useUserQuery } from "../../hooks/users/useUserQuery";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import AdminRoute from "../auth/RestrictedRoute";
import NewUser from "./NewUser";
import UsersTable from "./UsersTable";
import { cardStyle } from "../Places/PlacesTable";

const PAGE_SIZE = 10;
const DEFAULT_SORT = { field: "createdAt", order: "desc" };

export default function AllUsers() {
  const { token } = useCookie("vToken");
  const [addVisible, setAddVisible] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filters, setFilters] = useState<any[]>([]);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const search = useDebounce(searchQuery, 500);

  // Any filter, sort or search change goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [search, sort, filters]);

  const query = {
    _q: search,
    filters: {
      $and: [...filters, { role: { label: { $eq: "User" } } }],
    },
    pagination: { limit: PAGE_SIZE, page: currentPage },
    sort: `${sort.field}:${sort.order}`,
  };

  const endpoint = apiConfig.users.list(
    removeEmptyFilterQueries(qs.stringify(query, { encode: false })),
  );

  const { data, isLoading } = useQuery(
    ["getAllUsers", token, endpoint],
    () => apiService.MakeGetRequest(endpoint, token),
    { keepPreviousData: true },
  );

  const { removeUser, mutating } = useUserQuery();
  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      removeUser(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = data?.meta?.totalItems ?? 0;
  const activeFilters = filters.length;

  return (
    <AdminRoute>
      <section>
        {/* Title + count + add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Utilisateurs</h5>
            {data && (
              <Chip tone="warning">
                {total} {total > 1 ? "Utilisateurs" : "Utilisateur"}
              </Chip>
            )}
          </div>

          <button
            type="button"
            onClick={() => setAddVisible(true)}
            className="btn btn-primary text-white rounded-pill px-4 border-0"
          >
            + Ajouter un utilisateur
          </button>
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <div
            className="d-flex align-items-center gap-2 px-3 flex-grow-1"
            style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              placeholder="Rechercher un utilisateur"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersVisible(true)}
            className="btn btn-outline-danger rounded-pill d-flex align-items-center gap-2 px-3"
            style={{ height: 38 }}
          >
            <FilterIcon />
            Filtres{activeFilters > 0 && ` (${activeFilters})`}
          </button>

          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={mutating || selectedCount === 0}
            className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
          >
            {mutating ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              <>
                <DeleteIcon fill={colors.primary} /> Supprimer
                {selectedCount > 0 && ` (${selectedCount})`}
              </>
            )}
          </button>
        </div>

        <FiltersModel
          open={filtersVisible}
          onClose={() => setFiltersVisible(false)}
          configs={usersFilters}
          callback={(f: any[]) => setFilters(f)}
        />

        <CModal
          size="lg"
          alignment="center"
          visible={addVisible}
          onClose={() => setAddVisible(false)}
        >
          <NewUser onAccept={() => {}} onClose={() => setAddVisible(false)} />
        </CModal>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              data?.items?.length > 0 ? (
                <UsersTable
                  users={data.items as User[]}
                  UsersToBeDeletedList={dispatch}
                  sort={{
                    sort,
                    setSort,
                    resetSort: () => setSort(DEFAULT_SORT),
                  }}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={data?.meta?.totalPages}
          pageSize={PAGE_SIZE}
        />
      </section>
    </AdminRoute>
  );
}
